import { useState } from "react";
import { Globe, User } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { CompactHero } from "@/components/brand/CompactHero";
import { HeroMetaItem, HeroMetaSep } from "@/components/brand/Hero";
import { FieldCard, Field } from "@/components/brand/SettingsShell";
import { useToast } from "@/hooks/use-toast";
import { useAuthStore } from "@/store/authStore";

const LANGUAGE_OPTIONS: { value: string; label: string; flag: string }[] = [
  { value: "en-US", label: "English", flag: "🇺🇸" },
  { value: "pt-BR", label: "Português (Brasil)", flag: "🇧🇷" },
  { value: "es-ES", label: "Español", flag: "🇪🇸" },
];

export default function Profile() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user, setLanguage } = useAuthStore();

  const currentLang = user?.language || "en-US";
  const [selected, setSelected] = useState<string>(currentLang);
  const [saving, setSaving] = useState(false);

  const isDirty = selected !== currentLang;

  const handleSave = async () => {
    setSaving(true);
    try {
      await setLanguage(selected);
      toast({ title: t("Saved"), description: t("Language preference updated.") });
    } catch {
      toast({
        title: t("Error"),
        description: t("Failed to save language preference. Please try again."),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-6 pb-10">
      <CompactHero
        glyph={<User />}
        overline={t("Account")}
        title={user?.name || t("Profile")}
        meta={
          <>
            <HeroMetaItem k="email" v={user?.email || ""} />
            <HeroMetaSep />
            <HeroMetaItem k="role" v={user?.role || ""} />
          </>
        }
      />

      {/* O idioma, que é da pessoa e não do workspace.

          Havia um card de senha logo abaixo, trazido de `/admin/settings` — era o
          único de lá que não exigia ser admin, e por causa dele aquela tela
          inteira ficava aberta a todo mundo. Ele saiu com o login por senha: não
          há mais o que trocar, e `POST /api/auth/password` não existe. */}
      <div className="flex max-w-2xl flex-col gap-6">
        <FieldCard
          icon={<Globe />}
          title={t("Language")}
          sub={t("Choose the language used across the entire platform.")}
        >
          <Field
            label={t("Interface language")}
            hint={t("Changes take effect immediately after saving.")}
          >
            <div className="flex flex-col gap-2">
              {LANGUAGE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setSelected(opt.value)}
                  className={cn(
                    "w-full text-left px-4 py-3 rounded-lg border text-sm font-medium flex items-center gap-3 transition-colors cursor-pointer",
                    selected === opt.value
                      ? "border-[#ffc83d] bg-[rgba(255,200,61,0.08)] text-[#08254c] shadow-[inset_0_0_0_1px_#ffc83d]"
                      : "border-[#e0e6ed] text-[#596b85] bg-white hover:border-[#08254c] hover:text-[#08254c]",
                  )}
                >
                  <span className="text-lg leading-none">{opt.flag}</span>
                  <span>{opt.label}</span>
                  {selected === opt.value && (
                    <span className="ml-auto w-2 h-2 rounded-full bg-[#ffc83d]" />
                  )}
                </button>
              ))}
            </div>
          </Field>

          <div className="flex items-center justify-end pt-2 border-t border-[#f2f4f7]">
            <Button
              size="sm"
              disabled={!isDirty || saving}
              onClick={handleSave}
              className="bg-[#08254c] hover:bg-[#0a2f60] text-white"
            >
              {saving ? t("Saving…") : t("Save changes")}
            </Button>
          </div>
        </FieldCard>
      </div>
    </div>
  );
}
